import { Box, styled, Loader, Typography, Icon, LicensePlate } from "@tm/components"
import { useCountryCodeToLicensePlate, useSelectedVehicleLookup } from "@tm/context-distribution"
import { DateField, SuggestionTextField, TextField, VinField, VinFieldProps } from "@tm/controls"
import { useLocalization } from "@tm/localization"
import { RegistrationNoType, Vehicle } from "@tm/models"
import { getVehicleRegistrationNoLabelTextIds, useAvailableVehicleSearchOptions } from "@tm/utils"
import { useEffect, useMemo } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { ZodEffects, ZodObject, ZodRawShape } from "zod"

const Title = styled(Box)(({ theme }) => ({
    display: "flex",
    padding: theme.spacing(0.5, 0.5, 0.5, 0),
    alignItems: "center",
}))

const FieldsArea = styled(Box)(() => ({
    display: "flex",
    flex: 1,
    flexWrap: "wrap",
    flexBasis: "100%",
}))

const Col = styled(Box)(({ theme }) => ({
    width: "12em",
    padding: theme.spacing(0.5, 1, 0.5, 0),
    ".suggest__box .vin-search": {
        margin: "0 0 .5em 0",
    },
    ".suggest__box": {
        padding: "4.25em 0.1em 0.5em",
    },

    ".suggest__box .vin-search .btn": {
        textWrap: "wrap",
    },
    ".suggest ": {
        height: "100%",
    },
}))

export type GeneralVehicleFields = {
    initialRegistration?: Date
    mileage?: number
    plateNumber?: string
    vin?: string
    registrationNo?: string
    registrationNos?: string[]
    minDate?: Date
}

type Props = {
    onUpdateFieldValue: (path: string, value: string | Date | number) => void
    onFormValid(isValid: boolean): void
    fields: Partial<GeneralVehicleFields>
    vinFieldProps?: VinFieldProps
    vehicle?: Vehicle
    children?: React.ReactNode
    schema?: ZodObject<ZodRawShape> | ZodEffects<ZodObject<ZodRawShape>>
    showVehicleModelDetailsLoading?: boolean
    isGermanOrAustrianVehicle?: boolean
    disableRegistrationNo?: boolean
}

export default function GeneralVehicleForm(props: Props) {
    const {
        vehicle,
        fields,
        onUpdateFieldValue,
        vinFieldProps,
        children,
        onFormValid,
        schema,
        isGermanOrAustrianVehicle,
        disableRegistrationNo,
        showVehicleModelDetailsLoading,
    } = props
    const { translateText } = useLocalization()

    const { selectedVehicleLookup } = useSelectedVehicleLookup()
    const { availableVehicleSearches } = useAvailableVehicleSearchOptions()

    const defaultVehicleLookup = useMemo(
        () => availableVehicleSearches.configuredVehicleLookups.filter((lookup) => lookup.isDefault),
        [availableVehicleSearches]
    )

    const { plateCode: shortCountryCode } = useCountryCodeToLicensePlate(vehicle?.countryCode)
    const defaultFormValues = useMemo(() => ({ ...fields }), [fields])

    const {
        control,
        trigger,
        reset,
        formState: { isValid },
    } = useForm<GeneralVehicleFields>({
        resolver: schema ? zodResolver(schema) : undefined,
        defaultValues: defaultFormValues,
        mode: "onChange",
        reValidateMode: "onBlur",
    })

    useEffect(() => {
        reset(fields)
        trigger()
    }, [fields])

    useEffect(() => {
        onFormValid(isValid)
    }, [isValid])

    const renderRegNoField = () => {
        if (disableRegistrationNo) {
            return
        }

        const registarionTypeId =
            vehicle?.registrationTypeId === RegistrationNoType.TopmotiveVin ? RegistrationNoType.Kba : vehicle?.registrationTypeId
        let itemLabelTextIds = getVehicleRegistrationNoLabelTextIds(registarionTypeId || RegistrationNoType.Kba)

        if (!itemLabelTextIds?.label) {
            itemLabelTextIds = getVehicleRegistrationNoLabelTextIds(selectedVehicleLookup?.lookupTypeId ?? defaultVehicleLookup[0]?.lookupTypeId)
        }

        if (!itemLabelTextIds?.label) {
            return
        }

        return (
            <Col>
                <Controller
                    name="registrationNo"
                    control={control}
                    render={({ field, fieldState }) => (
                        <SuggestionTextField
                            floatingLabel
                            label={translateText(itemLabelTextIds?.label ?? "")}
                            value={field.value}
                            getSuggestionData={() => fields?.registrationNos ?? []}
                            onChangeConfirm={(value) => {
                                field.onChange(value)
                                onUpdateFieldValue(field.name, value || "")
                            }}
                            path={[field.name]}
                            formatter={(value) => value.toUpperCase()}
                            fieldState={fieldState}
                        />
                    )}
                />
            </Col>
        )
    }
    const handleGetInitialRegistration = (field: ControllerRenderProps<GeneralVehicleFields, "initialRegistration">, value: Date | null) => {
        try {
            field.onChange(value)
            onUpdateFieldValue(field.name, value ?? "")
        } catch (error) {
            onUpdateFieldValue(field.name, "")
        }
    }

    const renderVehicleFields = () => {
        return (
            <>
                <FieldsArea>
                    {renderRegNoField()}
                    <Col>
                        <Controller
                            name="initialRegistration"
                            control={control}
                            render={({ field, fieldState }) => (
                                <DateField
                                    {...field}
                                    useUtc
                                    size="l"
                                    floatingLabel
                                    label={translateText(124)}
                                    value={field?.value}
                                    minDate={fields?.minDate}
                                    openToDate={fields.initialRegistration || fields.minDate}
                                    onChange={(value) => handleGetInitialRegistration(field, value)}
                                    path={[field?.name]}
                                    fieldState={fieldState}
                                />
                            )}
                        />
                    </Col>
                    <Col sx={{ width: "15em" }}>
                        <Controller
                            name="vin"
                            control={control}
                            render={({ field, fieldState }) => (
                                <VinField
                                    {...vinFieldProps}
                                    {...field}
                                    value={field.value}
                                    floatingLabel
                                    coloredBorder
                                    path={[field.name]}
                                    label={translateText(101)}
                                    onChangeConfirm={(value) => onUpdateFieldValue(field.name, value)}
                                    onChange={(value) => {
                                        field.onChange(value)
                                        onUpdateFieldValue(field.name, value)
                                    }}
                                    fieldState={fieldState}
                                />
                            )}
                        />
                    </Col>
                    <Col sx={{ width: "13em" }}>
                        <Controller
                            name="plateNumber"
                            control={control}
                            render={({ field, fieldState }) => (
                                <LicensePlate
                                    countryCode={shortCountryCode}
                                    size="medium"
                                    maxLength={17}
                                    isReadOnly={false}
                                    {...field}
                                    onChange={(value) => {
                                        field.onChange(value)
                                        onUpdateFieldValue(field.name, value)
                                    }}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                    }}
                                    fieldState={fieldState}
                                />
                            )}
                        />
                    </Col>
                    <Col>
                        <Controller
                            name="mileage"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    size="s"
                                    label={translateText(125)}
                                    value={field.value || undefined}
                                    onChange={(value) => {
                                        field.onChange(Number(value))
                                        onUpdateFieldValue(field.name, +value)
                                    }}
                                    inputMode="numeric"
                                    fieldState={fieldState}
                                    pattern={/[\d.]*/}
                                    formatter={(value) => value.replace(/[^\d]/, "").replace(",", "").replace(".", "")}
                                    path={[field.name]}
                                />
                            )}
                        />
                    </Col>

                    {children}
                </FieldsArea>
                {isGermanOrAustrianVehicle ? (
                    <Typography fontWeight={600} sx={{ paddingLeft: ".5em" }}>
                        {translateText(13286)}
                    </Typography>
                ) : null}
            </>
        )
    }

    return (
        <Box>
            <Title>
                <Icon name="car-front" size="extralarge" />
                <Typography fontSize="large" marginLeft="0.5em">
                    {translateText(99)}
                </Typography>
            </Title>

            <Box>
                {showVehicleModelDetailsLoading && (
                    <>
                        <Loader size="small" />
                        <Typography>{translateText(12583)}</Typography>
                    </>
                )}

                {!showVehicleModelDetailsLoading && renderVehicleFields()}
            </Box>
        </Box>
    )
}
